import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Search, Loader2, Lightbulb, Building2, Package, Hash, Mail, Phone, MapPin, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [refinedQuery, setRefinedQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a search query");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/search`, {
        query: searchQuery,
        search_type: searchType
      });
      setResults(response.data.results);
      if (response.data.results.length === 0) {
        toast.info("No results found. Try refining your search.");
      } else {
        toast.success(`Found ${response.data.results.length} vendor(s)`);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAISuggestions = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a search query first");
      return;
    }

    setLoadingSuggestions(true);
    try {
      const response = await axios.post(`${API}/ai-suggestions`, {
        query: searchQuery,
        context: `Searching for ${searchType === 'all' ? 'all types' : searchType}`
      });
      setSuggestions(response.data.suggestions);
      setRefinedQuery(response.data.refined_query);
      toast.success("AI suggestions generated!");
    } catch (error) {
      console.error("AI suggestion error:", error);
      toast.error("Failed to generate suggestions");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const applyRefinedQuery = () => {
    setSearchQuery(refinedQuery);
    setSuggestions([]);
    setRefinedQuery("");
  };

  const applySuggestion = (suggestion) => {
    setSearchQuery(suggestion);
    setSuggestions([]);
  };

  const handleFindDistributors = async (companyName) => {
    setSearchQuery(companyName);
    setSearchType("distributors");
    setLoading(true);
    try {
      const response = await axios.post(`${API}/search`, {
        query: companyName,
        search_type: "distributors"
      });
      setResults(response.data.results);
      if (response.data.results.length === 0) {
        toast.info("No distributors found. Try refining your search.");
      } else {
        toast.success(`Found ${response.data.results.length} distributor(s)`);
      }
    } catch (error) {
      console.error("Distributor search error:", error);
      toast.error("Distributor search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white py-8 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <div className="bg-blue-500 p-3 rounded-lg">
              <Building2 className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>SUPPO's CAGE Procurement Helper</h1>
              <p className="text-blue-200 mt-1" style={{fontFamily: 'Inter, sans-serif'}}>Commercial And Government Entity Research Tool</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Section */}
        <Card className="mb-8 shadow-xl border-0" data-testid="search-card">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50">
            <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              <Search className="h-5 w-5 text-blue-600" />
              Vendor Search
            </CardTitle>
            <CardDescription style={{fontFamily: 'Inter, sans-serif'}}>Search by CAGE code, company name, or part number</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Search Type Tabs */}
            <Tabs value={searchType} onValueChange={setSearchType} className="mb-4">
              <TabsList className="grid w-full grid-cols-5 bg-slate-100" data-testid="search-type-tabs">
                <TabsTrigger value="all" data-testid="tab-all">
                  <span className="flex items-center gap-1">
                    <Search className="h-4 w-4" />
                    All
                  </span>
                </TabsTrigger>
                <TabsTrigger value="cage" data-testid="tab-cage">
                  <span className="flex items-center gap-1">
                    <Hash className="h-4 w-4" />
                    CAGE Code
                  </span>
                </TabsTrigger>
                <TabsTrigger value="company" data-testid="tab-company">
                  <span className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    Company
                  </span>
                </TabsTrigger>
                <TabsTrigger value="part" data-testid="tab-part">
                  <span className="flex items-center gap-1">
                    <Package className="h-4 w-4" />
                    Part Number
                  </span>
                </TabsTrigger>
                <TabsTrigger value="distributors" data-testid="tab-distributors">
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    US Distributors
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Search Input */}
            <div className="flex gap-2 mb-4">
              <Input
                data-testid="search-input"
                placeholder="Enter CAGE code, company name, part number, or vendor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
                style={{fontFamily: 'Inter, sans-serif'}}
              />
              <Button 
                data-testid="search-button"
                onClick={handleSearch} 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 px-6"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Searching
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
            </div>

            {/* Action Buttons Row */}
            <div className="flex gap-2 mb-0">
              <Button
                data-testid="ai-suggestions-button"
                onClick={handleAISuggestions}
                disabled={loadingSuggestions || !searchQuery}
                variant="outline"
                className="flex-1 border-blue-200 hover:bg-blue-50"
              >
                {loadingSuggestions ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Generating Suggestions...
                  </>
                ) : (
                  <>
                    <Lightbulb className="h-4 w-4 mr-2 text-yellow-500" />
                    Get AI Search Suggestions
                  </>
                )}
              </Button>
              
              <Button
                data-testid="find-distributors-button"
                onClick={() => handleFindDistributors(searchQuery)}
                disabled={loading || !searchQuery}
                variant="outline"
                className="flex-1 border-green-200 hover:bg-green-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Users className="h-4 w-4 mr-2 text-green-600" />
                    Find US Distributors
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* AI Suggestions Display */}
        {(suggestions.length > 0 || refinedQuery) && (
          <Card className="mb-8 border-yellow-200 bg-yellow-50 shadow-lg" data-testid="suggestions-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                <Lightbulb className="h-5 w-5" />
                AI Suggestions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {refinedQuery && refinedQuery !== searchQuery && (
                <div className="mb-4 p-4 bg-white rounded-lg border border-yellow-200">
                  <p className="text-sm text-slate-600 mb-2" style={{fontFamily: 'Inter, sans-serif'}}>Refined Query:</p>
                  <div className="flex items-center gap-2">
                    <p className="flex-1 font-medium" style={{fontFamily: 'Inter, sans-serif'}}>{refinedQuery}</p>
                    <Button 
                      data-testid="apply-refined-query"
                      size="sm" 
                      onClick={applyRefinedQuery}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              )}
              
              {suggestions.length > 0 && (
                <div>
                  <p className="text-sm text-slate-600 mb-3" style={{fontFamily: 'Inter, sans-serif'}}>Alternative Searches:</p>
                  <div className="space-y-2">
                    {suggestions.map((suggestion, index) => (
                      <div 
                        key={index} 
                        className="flex items-center gap-2 p-3 bg-white rounded-lg border border-yellow-200 hover:border-yellow-400 transition-colors cursor-pointer"
                        onClick={() => applySuggestion(suggestion)}
                        data-testid={`suggestion-${index}`}
                      >
                        <Search className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <p className="flex-1" style={{fontFamily: 'Inter, sans-serif'}}>{suggestion}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-4 text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Search Results ({results.length})
            </h2>
            <div className="grid gap-6">
              {results.map((vendor, index) => (
                <Card key={index} className="shadow-lg hover:shadow-xl transition-shadow" data-testid={`vendor-result-${index}`}>
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <CardTitle className="text-xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>{vendor.company_name}</CardTitle>
                        {vendor.cage_code && (
                          <Badge className="mt-2 bg-blue-600" data-testid={`cage-code-${index}`}>
                            CAGE: {vendor.cage_code}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {vendor.url && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              const url = vendor.url.startsWith('http') ? vendor.url : `https://${vendor.url}`;
                              window.open(url, '_blank');
                            }}
                            data-testid={`visit-website-${index}`}
                          >
                            Visit Website
                          </Button>
                        )}
                        {vendor.cage_code && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    // Copy CAGE code to clipboard
                                    navigator.clipboard.writeText(vendor.cage_code);
                                    toast.success(`CAGE code ${vendor.cage_code} copied! Opening GSA 889...`);
                                    // Open GSA 889 site
                                    setTimeout(() => {
                                      window.open('https://889.smartpay.gsa.gov/#/', '_blank');
                                    }, 500);
                                  }}
                                  data-testid={`gsa-889-${index}`}
                                  className="border-purple-200 hover:bg-purple-50"
                                >
                                  GSA 889 Check
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Click button, CAGE copied: Paste CAGE on 889 page</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleFindDistributors(vendor.company_name)}
                          data-testid={`find-distributors-${index}`}
                          className="border-green-200 hover:bg-green-50"
                        >
                          <Users className="h-4 w-4 mr-1" />
                          Find Distributors
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {vendor.email && (
                        <div className="flex items-start gap-3" data-testid={`email-${index}`}>
                          <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-slate-600" style={{fontFamily: 'Inter, sans-serif'}}>Email</p>
                            <p className="font-medium" style={{fontFamily: 'Inter, sans-serif'}}>{vendor.email}</p>
                          </div>
                        </div>
                      )}
                      
                      {vendor.main_phone && (
                        <div className="flex items-start gap-3" data-testid={`main-phone-${index}`}>
                          <Phone className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-slate-600" style={{fontFamily: 'Inter, sans-serif'}}>Main Phone</p>
                            <p className="font-medium" style={{fontFamily: 'Inter, sans-serif'}}>{vendor.main_phone}</p>
                          </div>
                        </div>
                      )}
                      
                      {vendor.sales_phone && (
                        <div className="flex items-start gap-3" data-testid={`sales-phone-${index}`}>
                          <Phone className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-slate-600" style={{fontFamily: 'Inter, sans-serif'}}>Sales Phone</p>
                            <p className="font-medium" style={{fontFamily: 'Inter, sans-serif'}}>{vendor.sales_phone}</p>
                          </div>
                        </div>
                      )}
                      
                      {vendor.business_address && (
                        <div className="flex items-start gap-3" data-testid={`address-${index}`}>
                          <MapPin className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-slate-600" style={{fontFamily: 'Inter, sans-serif'}}>Address</p>
                            <p className="font-medium" style={{fontFamily: 'Inter, sans-serif'}}>{vendor.business_address}</p>
                          </div>
                        </div>
                      )}
                      
                      {vendor.sales_contact && (
                        <div className="flex items-start gap-3" data-testid={`sales-contact-${index}`}>
                          <Users className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-slate-600" style={{fontFamily: 'Inter, sans-serif'}}>Sales Contact</p>
                            <p className="font-medium" style={{fontFamily: 'Inter, sans-serif'}}>{vendor.sales_contact}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {vendor.distributors && vendor.distributors.length > 0 && (
                      <>
                        <Separator className="my-4" />
                        <div data-testid={`distributors-${index}`}>
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-semibold text-slate-700" style={{fontFamily: 'Inter, sans-serif'}}>
                              Authorized US Distributors Found: {vendor.distributors.length}
                            </p>
                            {vendor.distributors.length > 1 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  toast.info(`Searching all ${vendor.distributors.length} distributors...`);
                                  const allResults = [];
                                  for (const dist of vendor.distributors.slice(0, 5)) {
                                    try {
                                      const response = await axios.post(`${API}/search`, {
                                        query: dist,
                                        search_type: "company"
                                      });
                                      allResults.push(...response.data.results);
                                    } catch (error) {
                                      console.error(`Error searching ${dist}:`, error);
                                    }
                                  }
                                  setResults(allResults);
                                  toast.success(`Found ${allResults.length} distributor details`);
                                }}
                                className="border-green-300 hover:bg-green-50"
                                data-testid={`search-all-distributors-${index}`}
                              >
                                <Search className="h-3 w-3 mr-1" />
                                View All Details
                              </Button>
                            )}
                          </div>
                          <div className="space-y-2">
                            {vendor.distributors.map((dist, i) => (
                              <div key={i} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center gap-2">
                                  <Users className="h-4 w-4 text-green-600" />
                                  <span className="font-medium text-sm" style={{fontFamily: 'Inter, sans-serif'}}>{dist}</span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={async () => {
                                    toast.info(`Searching for ${dist}...`);
                                    setSearchQuery(dist);
                                    setSearchType("company");
                                    try {
                                      const response = await axios.post(`${API}/search`, {
                                        query: dist,
                                        search_type: "company"
                                      });
                                      setResults(response.data.results);
                                      if (response.data.results.length > 0) {
                                        toast.success(`Found details for ${dist}`);
                                      } else {
                                        toast.warning(`No details found for ${dist}`);
                                      }
                                    } catch (error) {
                                      console.error("Search error:", error);
                                      toast.error("Search failed");
                                    }
                                  }}
                                  className="text-xs hover:bg-green-100"
                                  data-testid={`search-distributor-${i}`}
                                >
                                  <Search className="h-3 w-3 mr-1" />
                                  View Details
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    
                    {vendor.source_url && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs text-slate-500" style={{fontFamily: 'Inter, sans-serif'}}>Source: {vendor.source_url}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && results.length === 0 && (
          <Card className="shadow-lg" data-testid="empty-state">
            <CardContent className="pt-16 pb-16 text-center">
              <Search className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>No Results Yet</h3>
              <p className="text-slate-500" style={{fontFamily: 'Inter, sans-serif'}}>Enter a CAGE code, company name, or part number to begin your search</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Footer */}
      <div className="bg-slate-900 text-white py-6 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400" style={{fontFamily: 'Inter, sans-serif'}}>SUPPO's CAGE Procurement Helper</p>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;